import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Boxes,
  Tag,
  Truck,
  FileText,
  LayoutGrid,
  LogOut,
  Bell,
  Menu,
  X,
  UserCog,
  Calendar,
  Film,
  Mail,
  MessageCircle,
  Utensils,
  KeyRound,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, perm: "stats.view" },
  { to: "/admin/produits", label: "Products", icon: Package, perm: "products.view" },
  { to: "/admin/sides", label: "Side dishes", icon: Utensils, perm: "products.manage" },
  { to: "/admin/commandes", label: "Orders", icon: ShoppingCart, perm: "orders.view" },
  { to: "/admin/clients", label: "Customers", icon: Users, perm: "customers.view" },
  { to: "/admin/stock", label: "Stock", icon: Boxes, perm: "stock.view" },
  { to: "/admin/promotions", label: "Promotions", icon: Tag, perm: "promotions.manage" },
  { to: "/admin/livraison", label: "Delivery", icon: Truck, perm: "content.edit" },
  { to: "/admin/contenu", label: "Homepage copy", icon: FileText, perm: "content.edit" },
  { to: "/admin/cms", label: "Site content", icon: LayoutGrid, perm: "cms.edit" },
  { to: "/admin/team", label: "Team", icon: UserCog, perm: "cms.edit" },
  { to: "/admin/events/devis", label: "Event Quotes", icon: Calendar, perm: "events.manage" },
  { to: "/admin/events/realisations", label: "Event Portfolio", icon: Film, perm: "events.manage" },
  { to: "/admin/avis", label: "Reviews moderation", icon: MessageCircle, adminOnly: true },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail, adminOnly: true },
  { to: "/admin/managers", label: "Managers", icon: UserCog, adminOnly: true },
  { to: "/admin/compte", label: "My account", icon: KeyRound },
];

import NewOrderNotifier from "@/components/admin/NewOrderNotifier";
import PushPermissionBanner from "@/components/admin/PushPermissionBanner";

export default function AdminLayout() {
  const { user, logout, isAdmin, hasPerm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  // Filter NAV by permissions
  const visibleNav = NAV.filter((n) => {
    if (n.adminOnly) return isAdmin;
    if (!n.perm) return true;
    return hasPerm(n.perm);
  });

  const currentNav = visibleNav.find((n) => n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)) || visibleNav[0] || NAV[0];

  const NavList = ({ onClick }) => (
    <nav className="flex-1 py-4 overflow-y-auto min-h-0">
      {visibleNav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onClick}
          className={({ isActive }) => `admin-nav-link ${isActive ? "active" : ""}`}
          data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
        >
          <n.icon size={16} />
          <span>{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-[#F4F6F5]">
      {/* DESKTOP Sidebar (lg and up) */}
      <aside className="hidden lg:flex w-64 bg-[var(--dd-green)] text-white flex-col fixed inset-y-0 z-40" data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/10">
          <Link to="/admin" className="block">
            <div className="font-bold text-xl leading-tight">Dakar Street Food</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dd-mustard)] mt-1">Admin Console</div>
          </Link>
        </div>
        <NavList />
        <div className="p-6 border-t border-white/10">
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            className="flex items-center gap-2 text-sm text-white/70 hover:text-[var(--dd-mustard)] transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* MOBILE Drawer */}
      {mobileNavOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileNavOpen(false)}
            data-testid="admin-mobile-overlay"
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-72 bg-[var(--dd-green)] text-white flex flex-col z-50 shadow-2xl" data-testid="admin-mobile-sidebar">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <Link to="/admin" className="block">
                <div className="font-bold text-lg leading-tight">Dakar Street Food</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dd-mustard)] mt-1">Admin Console</div>
              </Link>
              <button onClick={() => setMobileNavOpen(false)} className="text-white p-1" data-testid="admin-mobile-close">
                <X size={22} />
              </button>
            </div>
            <NavList onClick={() => setMobileNavOpen(false)} />
            <div className="p-5 border-t border-white/10">
              <button
                onClick={() => { logout(); navigate("/admin/login"); }}
                className="flex items-center gap-2 text-sm text-white/70 hover:text-[var(--dd-mustard)] transition-colors w-full"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Topbar */}
        <header className="bg-white border-b border-[var(--dsf-border)] sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 py-3 lg:py-4 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-[var(--dd-bone)] text-[var(--dd-green)]"
                data-testid="admin-mobile-toggle"
              >
                <Menu size={20} />
              </button>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dsf-text-muted)] hidden md:block">Console</div>
                <div className="font-bold text-base md:text-xl text-[var(--dd-green)] truncate">
                  <span className="lg:hidden">{currentNav.label}</span>
                  <span className="hidden lg:inline">Hi, {user?.name}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-[var(--dd-green)] rounded-full px-1.5 py-1">
                <NewOrderNotifier />
              </div>
              <Link to="/" className="text-xs lg:text-sm text-[var(--dsf-text-secondary)] hover:text-[var(--dd-mustard)] whitespace-nowrap" data-testid="admin-view-site">
                <span className="lg:hidden">View →</span>
                <span className="hidden lg:inline">View site →</span>
              </Link>
            </div>
          </div>
        </header>

        <PushPermissionBanner />

        <main className="p-4 lg:p-8" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
